/**
 * THE SCALE PALETTE — owner-directed, 2026-09-15 (fourth cut).
 *
 * Every chart on this screen colours its marks by MAGNITUDE — a vendor's licence spend, a
 * cost component, an application's run rate or saving, a renewal's licence, an area's
 * application count, a disposition's share of run rate — on one four-step ramp, green for
 * the smallest through amber and orange to red for the largest. Bars and circles use the
 * same ramp, so switching the view never changes what a colour means. Applied to ALL FOUR
 * tabs, at the owner's direction; what a mark stands for is carried by its label, its legend
 * chip and its tooltip, not by its colour.
 *
 * The four hues are the Disposition Model's (Retain green, Re-Design amber, Re-Platform
 * orange, Retire red) taken at their SOFT side. Three cuts were rejected on the way here —
 * a slate ("like someone died"), a pastel ramp, and white-mixed matrix colours that were
 * "hard" and whose red "is not red but some kind of pink" (#dc3545 is a crimson, and mixing
 * white into it leaves a blue cast). So the four are fixed tokens, each a pure hue at a
 * light tone — Material's 300 steps, which is what "soft side of the same colour" means in
 * the design language this platform already uses (§1.9):
 *
 *   green   #81c784   hue 122°
 *   amber   #ffd54f   hue  46°
 *   orange  #ffb74d   hue  36°
 *   red     #e57373   hue   0°  — a red, not a crimson: no blue in it
 *
 * Ink on each is the fill's own hue darkened until it measures 4.5:1 ON THAT FILL.
 */

export interface ScaleColor { fill: string; ink: string }

type RGB = [number, number, number];

function parse(hex: string): RGB {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
const toHex = (c: RGB) => `#${c.map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('')}`;
function luminance([r, g, b]: RGB): number {
  const f = (v: number) => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}
const contrast = (a: RGB, b: RGB) => { const x = luminance(a), y = luminance(b); return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05); };

/** White where it clears 4.5:1 on the fill; otherwise the fill's own hue, darkened until it
 *  does. Measured, not picked. */
function inkOn(fill: RGB): string {
  if (contrast(fill, [255, 255, 255]) >= 4.5) return '#ffffff';
  let k = 0.6;
  let ink: RGB = [fill[0] * k, fill[1] * k, fill[2] * k];
  while (contrast(fill, ink) < 4.5 && k > 0.05) {
    k -= 0.05;
    ink = [fill[0] * k, fill[1] * k, fill[2] * k];
  }
  return toHex(ink);
}

/*
 * FIFTH CUT (owner-directed, 2026-10-23): "a different set of colours, like the one used on
 * the Portfolio Definition Executive Dashboard". The ramp is now that dashboard's sequential
 * blue — `--ix-s1`…`--ix-s4` and `--ix-s6` in
 * pages/portfolio_definition/executive/ExecutiveDashboard.css — light for the smallest, deep
 * for the largest. The four-hue ramp above is kept in this comment as the record of why it
 * was chosen; the meaning (colour = magnitude, the same on every tab) is unchanged.
 */
const STEPS: ScaleColor[] = ['#b9d3e6', '#86b4d3', '#5291bd', '#2a6f9f', '#0b3f62'].map((hex) => ({ fill: hex, ink: inkOn(parse(hex)) }));

export type Scale = (value: number, max: number) => ScaleColor;

/** The colour for `value` on a scale whose largest member is `max`. */
export const scaleColor: Scale = (value, max) => {
  if (!(max > 0) || !(value > 0)) return STEPS[0]!;
  /* Square-rooted so the long tail of small values does not all land on green — the same
     reason bubble AREA, not radius, carries the value. */
  const t = Math.sqrt(Math.min(1, value / max));
  return STEPS[Math.min(STEPS.length - 1, Math.round(t * (STEPS.length - 1)))]!;
};

/** The ramp itself, smallest first — for a legend. */
export const SCALE_STEPS = STEPS;
